import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";

const headers: Readonly<Record<string, string | boolean>> = {
  "Content-Type": "application/json; charset=utf-8",
};

export type AnnaResponseRaw<T> = {
  data: T;
};

// Axios rejects with the HTTP response (see the interceptor below), so caught errors look like this.
export type ApiErrorResponse = {
  status: number;
  data?: unknown;
};

export const isApiErrorResponse = (error: unknown): error is ApiErrorResponse =>
  typeof error === "object" && error !== null && "status" in error && typeof error.status === "number";

export class Api {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    if (this.instance === null) {
      throw Error("Api error: public url not exist");
    }
    return this.instance;
  }

  constructor(baseUrl: string) {
    this.init(baseUrl);
  }

  init(baseURL: string): AxiosInstance {
    const http = axios.create({
      baseURL,
      headers,
    });

    http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { response } = error;
        return await Api.handleError(response);
      },
    );

    this.instance = http;
    return http;
  }

  async request<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return await this.http.request(config);
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return await this.http.get<T>(url, config);
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return await this.http.post<T>(url, data, config);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return await this.http.put<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return await this.http.delete<T>(url, config);
  }

  static async handleError(response: unknown): Promise<never> {
    // Callers inspect the rejected HTTP response (status, data), see isApiErrorResponse
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the response object is the error contract
    return await Promise.reject(response);
  }

  async getFile(url: string): Promise<Blob> {
    const response = await this.request<Blob>({
      url,
      method: "GET",
      responseType: "blob",
    });

    return response.data;
  }
}
